import React from "react";
import { Container, Row, Col, Card, CardBody, Button, Label, FormGroup, Alert } from "reactstrap";
import PageTitle from "../../components/Common/PageTitle";
import AvForm from "availity-reactstrap-validation/lib/AvForm";
import AvField from "availity-reactstrap-validation/lib/AvField";
import { Link } from "react-router-dom";
import Loader from "../../shared/loader/loader";
import { useProductHook } from "./productHook";
import Select from "react-select";

const AddEditProduct = (props) => {
  const {
    saveProductHandler,
    loader,
    pageTitle,
    product,
    toggleSwitch,
    settoggleSwitch,
    subjects,
    productType,
    setProductType,
    permissionOptions,
    selectedPermissions,
    setSelectedPermissions,
    setProduct,
    grantablePermissions,
  } = useProductHook(props);

  const permissionMapping = {
    subject: { parents: ['subject', 'pastpaper', 'note', 'studyguide', 'task', 'tasks', 'mcqanswer', 'mcqanswers', 'assessment', 'assessments'] },
    past_paper: { parents: ['pastpaper'] },
    assessment: { parents: ['task', 'tasks', 'mcqanswer', 'mcqanswers', 'assessment', 'assessments'] },
    study_guide: { parents: ['studyguide', 'studyguides'] },
  };

  const generateName = (type, subjectId) => {
    if (!type || !subjectId) return "";
    const subject = subjects.find(s => s._id === subjectId);
    if (!subject) return "";

    if (type === "subject") return subject.title;
    if (type === "past_paper") return `${subject.title} Past Paper`;
    if (type === "assessment") return `${subject.title} Assessment`;
    if (type === "study_guide") return `${subject.title} Study Guide`;
    return "";
  };

  const typeOptions = [
    { value: "subject", label: "Subject" },
    { value: "past_paper", label: "Past Paper" },
    { value: "assessment", label: "Assessment" },
    { value: "study_guide", label: "Study Guide" },
  ];

  const handleTypeChange = (selectedOptions) => {
    const selectedTypes = (selectedOptions || []).map(o => o.value);
    setProductType(selectedTypes);

    const isSubject = selectedTypes.includes('subject');
    let subjectId = isSubject ? product.contentId : product.parentContentId;

    // Use the primary type for name generation (usually the first one selected)
    const primaryType = selectedTypes[0] || "";
    const newName = generateName(primaryType, subjectId);

    // Auto-select permissions (Aggregate from all selected types)
    let aggregatedPermissions = [];
    selectedTypes.forEach(type => {
      const typePermissions = permissionMapping[type];
      if (typePermissions && grantablePermissions.length > 0) {
        const matching = grantablePermissions
          .filter(p => {
            const parentLower = (p.parent || "").toLowerCase();
            return typePermissions.parents.some(allowed => allowed.toLowerCase() === parentLower) && p.routePath !== 'delete';
          })
          .map(p => ({
            value: p._id,
            label: `Access to: ${p.parent.charAt(0).toUpperCase() + p.parent.slice(1)}-${p.routePath}`
          }));
        aggregatedPermissions = [...aggregatedPermissions, ...matching];
      }
    });

    // Remove duplicates from aggregated permissions
    const uniquePermissions = Array.from(new Set(aggregatedPermissions.map(p => p.value)))
      .map(id => aggregatedPermissions.find(p => p.value === id));

    setSelectedPermissions(uniquePermissions);

    setProduct(prev => ({
      ...prev,
      type: selectedTypes,
      name: newName || prev.name
    }));
  };

  const handleSubjectChange = (e, isParent = false) => {
    const subjectId = e.target.value;
    const fieldName = isParent ? 'parentContentId' : 'contentId';

    // Generate name based on the first selected type
    const primaryType = productType[0] || "";
    const newName = generateName(primaryType, subjectId);

    setProduct(prev => ({
      ...prev,
      [fieldName]: subjectId,
      name: newName || prev.name
    }));
  };

  const isProductInUse = product.isPurchased || false;
  const isSubjectType = productType.includes('subject');

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid className="relative">
          <Loader loading={loader} />
          <PageTitle title={pageTitle} />

          {isProductInUse && (
            <Alert color="info">
              This product has been purchased. Only the <strong>Name, Description,</strong> and <strong>Status</strong> can be changed.
            </Alert>
          )}

          <AvForm
            className="needs-validation"
            model={product}
            onValidSubmit={(e, v) => {
              // Inject the correct types array into the values before saving
              const finalValues = { ...v, type: productType };
              saveProductHandler(e, finalValues);
            }}
          >
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <h4 className="card-title">Product Information</h4>
                    <p className="card-title-desc">Fill all information below</p>
                    <Row className="agora-lato">
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="type">Product Type(s)</Label>
                          <Select
                            isMulti
                            options={typeOptions}
                            value={typeOptions.filter(o => productType.includes(o.value))}
                            onChange={handleTypeChange}
                            placeholder="Select Type(s)..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isDisabled={isProductInUse}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                          <AvField
                            type="hidden"
                            name="type_validator"
                            value={productType}
                            validate={{ required: { value: true, errorMessage: "Please select at least one type" } }}
                          />
                        </FormGroup>
                      </Col>

                      {productType.length > 0 && isSubjectType && (
                        <Col md="6">
                          <FormGroup>
                            <Label htmlFor="contentId">Select Subject</Label>
                            <AvField
                              type="select"
                              name="contentId"
                              className="form-control"
                              errorMessage="Please select a subject"
                              validate={{ required: { value: true } }}
                              value={product.contentId}
                              disabled={isProductInUse}
                              onChange={(e) => handleSubjectChange(e, false)}
                            >
                              <option value="">Select Subject</option>
                              {subjects.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                            </AvField>
                          </FormGroup>
                        </Col>
                      )}

                      {productType.length > 0 && !isSubjectType && (
                        <Col md="6">
                          <FormGroup>
                            <Label htmlFor="parentContentId">Parent Subject</Label>
                            <AvField
                              type="select"
                              name="parentContentId"
                              className="form-control"
                              errorMessage="Please select a parent subject"
                              validate={{ required: { value: true } }}
                              value={product.parentContentId}
                              disabled={isProductInUse}
                              onChange={(e) => handleSubjectChange(e, true)}
                            >
                              <option value="">Select Parent Subject</option>
                              {subjects.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                            </AvField>
                          </FormGroup>
                        </Col>
                      )}

                      {/* --- Product Name now shown after type selection --- */}
                      {productType.length > 0 && (
                        <Col md="6">
                          <FormGroup>
                            <Label htmlFor="name">Product Name</Label>
                            <AvField name="name" placeholder="Enter product name" type="text" errorMessage="Please enter a product name" className="form-control" validate={{ required: { value: true } }} value={product.name} />
                          </FormGroup>
                        </Col>
                      )}

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="price">Price (PKR)</Label>
                          <AvField name="price" placeholder="Enter price" type="number" errorMessage="Please enter a valid price" validate={{ required: { value: true }, min: { value: 0, errorMessage: "Price cannot be negative" } }} value={product.price} />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="accessDurationDays">Access Duration (in Days)</Label>
                          <AvField name="accessDurationDays" placeholder="e.g., 365" type="number" errorMessage="Please provide the access duration in days" validate={{ required: { value: true }, min: { value: 1, errorMessage: "Duration must be at least 1 day" } }} value={product.accessDurationDays} disabled={isProductInUse} />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="grantedRoleIds">Granted Permissions</Label>
                          <Select
                            isMulti
                            isSearchable
                            options={permissionOptions}
                            value={selectedPermissions}
                            onChange={(selectedOptions) => {
                              setSelectedPermissions(selectedOptions || []);
                            }}
                            placeholder="Select one or more permissions..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isDisabled={isProductInUse}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                          <AvField
                            type="hidden"
                            name="grantedRoleIds"
                            value={selectedPermissions.map(p => p.value)}
                            validate={{ required: { value: true, errorMessage: "Please select at least one permission" } }}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <Label htmlFor="status">Status</Label>
                        <div className="custom-control status-switch custom-switch mb-2" dir="ltr">
                          <input type="checkbox" className="custom-control-input" id="status" checked={toggleSwitch} readOnly />
                          <label className="custom-control-label" htmlFor="status" onClick={() => { settoggleSwitch(!toggleSwitch); }}>
                            {toggleSwitch ? "Active" : "Inactive"}
                          </label>
                        </div>
                      </Col>

                      {/* --- NEW DESCRIPTION FIELD --- */}
                      <Col md="12">
                        <FormGroup>
                          <Label htmlFor="description">Description</Label>
                          <AvField
                            type="textarea"
                            name="description"
                            className="form-control"
                            rows="4"
                            placeholder="Enter a description for the product..."
                            value={product.description}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col lg="12" className="agora-form-buttons text-right">
                <Link to="/products-list" className="btn btn-primary agora-button cancel-button border-radius waves-effect waves-light">
                  Cancel
                </Link>
                <Button type="submit" className="btn btn-primary agora-button save-button border-radius waves-effect waves-light">
                  Save
                </Button>
              </Col>
            </Row>
          </AvForm>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddEditProduct;